/**
 * Cliente del backoffice para emails enviados (`email_sends`).
 *
 * Endpoints backend (`apps/backend/src/routes/admin/emails.ts`):
 *   - GET /admin/emails?…filtros…   → EmailSendItem[] + meta.nextCursor
 *   - GET /admin/emails/stats?from= → EmailStats
 *
 * Envelope `{ success, data, meta? }` + auth por cookie admin. Reusa `_core.js`.
 *
 * Alcance: `email_sends` sólo registra engagement/marketing + crédito. No
 * incluye OTP/verificación/reset/welcome (van directo) ni el contenido del
 * email (subject/body/eventos no se persisten). Ver docs/speak-plan/29.
 */
import { get } from './_core.js';

export const EMAIL_STATUS_VALUES = ['sent', 'pending'] as const;
export type EmailStatus = (typeof EMAIL_STATUS_VALUES)[number];

export interface EmailSendItem {
  id: string;
  userId: string;
  userEmail: string | null;
  kind: string;
  periodKey: string;
  status: string;
  sentAt: string | null;
  createdAt: string;
}

export interface EmailSendDetail extends EmailSendItem {
  /** Destinatario snapshot al enviar. Null en filas históricas/pending. */
  toEmail: string | null;
  /** Contenido EXACTO capturado al enviar. Null si no se capturó (histórico/pending). */
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
}

export interface EmailFilters {
  kind?: string;
  status?: string;
  userId?: string;
  q?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface EmailListResponse {
  items: EmailSendItem[];
  nextCursor: string | undefined;
}

export interface EmailKindCount {
  kind: string;
  count: number;
}

export interface EmailStats {
  total: number;
  sent: number;
  pending: number;
  byKind: EmailKindCount[];
}

export async function fetchEmails(filters: EmailFilters = {}): Promise<EmailListResponse> {
  const params = new URLSearchParams();
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.status) params.set('status', filters.status);
  if (filters.userId) params.set('user_id', filters.userId);
  if (filters.q) params.set('q', filters.q);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  const res = await get<EmailSendItem[]>(`/admin/emails${qs ? `?${qs}` : ''}`);
  return { items: res.data, nextCursor: res.meta?.nextCursor };
}

export async function fetchEmailStats(from?: string): Promise<EmailStats> {
  const qs = from ? `?from=${encodeURIComponent(from)}` : '';
  const res = await get<EmailStats>(`/admin/emails/stats${qs}`);
  return res.data;
}

export async function fetchEmail(id: string): Promise<EmailSendDetail> {
  const res = await get<EmailSendDetail>(`/admin/emails/${encodeURIComponent(id)}`);
  return res.data;
}

/**
 * Labels legibles por `kind`. Display-only: fallback al kind crudo si no está
 * mapeado (es cosmético, mostrar el kind sigue siendo informativo). Mantener
 * en sync con `apps/backend/src/lib/email/templates/`.
 */
const KIND_LABELS: Record<string, string> = {
  welcome: 'Bienvenida',
  'credit.low': 'Crédito bajo',
  'credit.empty': 'Crédito agotado',
  'streak.reminder': 'Recordatorio de racha',
  'milestone.streak': 'Hito · racha',
  'milestone.level': 'Hito · nivel',
  'milestone.unit': 'Hito · unidad',
  weekly: 'Resumen semanal',
  winback: 'Win-back',
  'content.announcement': 'Novedades Audioteca',
  'content.news': 'Novedades',
};

export function emailKindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** Categoría derivada del kind, para badge/filtro (transaccional vs marketing). */
const TRANSACTIONAL_KINDS = new Set(['welcome', 'credit.low', 'credit.empty']);

export function emailKindCategory(kind: string): 'transaccional' | 'marketing' {
  return TRANSACTIONAL_KINDS.has(kind) ? 'transaccional' : 'marketing';
}
